function copyNode( from, to ) {
	to.innerHTML = from.innerHTML;

	for ( let attribute of from.attributes ) {
		to.setAttribute( attribute.name, attribute.value );
	}
}

function resetInput( elem ) {
	if ( elem.type ) {
		switch ( elem.type ) {
			case 'radio':
			case 'checkbox':
				elem.removeAttribute( 'checked' );
				break;
			case 'hidden':
				// no-op
				break;
			default:
				elem.value = '';
				break;
		}
	}
}

function getDelegateTarget( e, selector, root = document.documentElement ) {
	let node = e.target;

	while ( node && node !== root ) {
		if ( node.matches( selector ) ) {
			return node;
		}

		node = node.parentNode;
	}

	return null;
}

export class AdvancedQuery {
	constructor( node, opts = {} ) {

		this.node = node;
		this.opts = {
			ajax: true,
			resultsSet: '.advanced-query__result-set',
			results: '.advanced-query__results',
			facets: '.advanced-query__facets',
			filters: '.facet__link',
			reset: '.facet__reset',
			resultsCount: '.results-count',
			pagination: '.advanced-query__pagination',
			paginationLink: '.advanced-query__pagination a',
			paginationMore: '.advanced-query__pagination-more',
			openedClass: 'facets--opened',
			opener: '.facets__heading--mobile',
			...opts,
		};

		this.reset = this.node.querySelector( this.opts.reset );
		this.results = this.node.querySelector( this.opts.results );
		this.resultsSet = this.node.querySelector( this.opts.resultsSet );
		this.resultsCount = this.node.querySelector( this.opts.resultsCount );
		this.pagination = this.node.querySelector( this.opts.pagination );
		this.filters = this.node.querySelectorAll( this.opts.filters );
		this.filtersContainer = this.node.querySelector( this.opts.filtersContainer );
		this.page = 1;

		// handle resest
		this.reset && this.reset.addEventListener( 'click', e => {
			e.preventDefault();
			[...this.filters].map(f => f.checked = false);
			this.page = 1;
			this.fetch( this.url() );
			this.resultsCount.innerHTML = '';
		});

		// handle toggling of a filter
		this.node.addEventListener( 'change', ( { target } ) => {
			if ( [ 'checkbox', 'radio' ].includes( target.type ) ) {
				this.page = 1;
				this.wasFocused = `[name="${ target.name }"][value="${ target.value }"]`;
				this.fetch( this.url() );
				[...this.filters].map(f => {
					if (f !== target) { f.checked = false }
				});
			}
		} );

		// handle pagination
		this.node.addEventListener( 'click', ( e ) => {
			const node = getDelegateTarget( e, this.opts.paginationLink, this.node );

			if ( node ) {
				e.preventDefault();
				this.fetch( e.target.href );
			}
		} );

		// handle load more
		this.node.addEventListener( 'click', ( e ) => {
			const node = getDelegateTarget( e, this.opts.paginationMore, this.node );

			if ( node ) {
				e.preventDefault();
				this.page++;
				this.fetch( this.url(), true );
			}
		} );

		if ( this.opts.ajax ) {
			// handle back button
			window.addEventListener( 'popstate', ( e ) => this.fetch( location.href, false ) );
		}
	}

	url() {
		let { origin, pathname } = location;

		// Remove existing page from pretty URLs
		pathname = pathname.replace(/\/page\/\d+/, '');

		const formData = new FormData(this.node);
		const formSearchParams = new URLSearchParams(formData);
		const urlSearchParams = new URLSearchParams(location.search);

		// remove any fields from the url params that overlap with fields on the form
		Array.from( this.node.elements ).map( el => el.name )
			.filter(Boolean).forEach( name => urlSearchParams.delete( name ) );

		// add form params to the url params
		for ( let param of formSearchParams.keys() ) {
			const values = formSearchParams.getAll( param );
			for ( let value of values ) {
				if (urlSearchParams.has( param, value ) ) {
					continue;
				}
				urlSearchParams.set( param, value );
				// urlSearchParams.append( param, value );
			}
		}

		// Handle pagination
		if (this.page > 1) {
			// Check if site uses pretty URLs (you may need to add a configuration option for this)
			const usesPrettyUrls = true; // This could be an option passed to the constructor

			if (usesPrettyUrls) {
				pathname = `${pathname.replace(/\/$/, '')}/page/${this.page}/`;
			} else {
				urlSearchParams.set('paged', this.page);
			}
		} else {
			urlSearchParams.delete('paged');
		}

		// remove empty values
		for ( let [ param, value ] of urlSearchParams.entries() ) {
			if ( ! value ) {
				urlSearchParams.delete( param, value );
			}
		}


		const hasQueryString = Array.from(urlSearchParams.keys()).length > 0;

		return `${origin}${pathname}${hasQueryString ? '?' : ''}${urlSearchParams}`;
	}

	async fetch( url, append = false ) {

		if ( ! this.opts.ajax ) {
			window.location.href = url;
			return;
		}

		this.node.classList.add( 'is-loading' );

		return fetch( url )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw response;
				}

				return response.text();
			} )
			.then( ( html ) => {

				// @see - https://developer.mozilla.org/en-US/docs/Web/API/DOMParser
				const parser = new DOMParser();
				const doc = parser.parseFromString( html, 'text/html' );
				const resultsCount = doc.querySelector( this.opts.resultsCount );
				const results = doc.querySelector( this.opts.results );
				const facets = doc.querySelector( this.opts.facets );
				const pagination = doc.querySelector( this.opts.pagination );

				if ( append ) {
					this.results &&	this.results.insertAdjacentHTML( 'beforeend', results.innerHTML );
				} else {
					results && copyNode( results, this.results );
					// facets && copyNode( results, this.facets );
				}

				if ( pagination ) {
					this.pagination.style.display = '';
					copyNode( pagination, this.pagination );
				} else {
					this.pagination.style.display = 'none';
				}

				if (resultsCount) {
					copyNode( resultsCount, this.resultsCount );
				}

				this.node.dispatchEvent( new CustomEvent( 'advanced-query:reload', { bubbles: true, cancelable: false } ) );

				if ( ! append ) {
					window.history.pushState( {}, '', url );
				}
			} )
			.catch( ( fault ) => {
				if ( fault instanceof Response && 404 === fault.status ) {
					this.results.innerHTML = `No results found for your filters. Please try again.`;
					this.pagination.innerHTML = '';
					return;
				}

				window.location.href = url;
			} )
			.finally( () => {
				this.node.classList.remove( 'is-loading' );
				if ( ! append && this.node.getBoundingClientRect().top < 0 ) {
					this.node.scrollIntoView( { behavior: 'smooth' } );
				}
			} );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	Array.from( document.querySelectorAll( '.advanced-query' ) ).map( ( node ) => new AdvancedQuery( node ) );
} );
